import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { getSpeechRecognitionCtor, type SpeechRecognitionLike } from './speech-recognition-types';
import { NativeSpeechRecognition } from './native-speech-recognition';
import { useMicLevel } from './mic-level';
import { detectSpeechEnv, type SpeechEnv } from './speech-env';

const RESTART_DELAY_MS = 300;
/** Une fois une erreur AFFICHÉE on continue d'essayer (une coupure réseau se répare seule), mais sans marteler le service. */
const SLOW_RESTART_DELAY_MS = 5000;

/** Refus définitif — inutile de relancer, l'utilisateur doit agir (permission navigateur). */
const FATAL_ERRORS = new Set(['not-allowed', 'service-not-allowed']);
/** Toujours bénin (fréquent en usage normal, `onend` relance déjà) — jamais compté ni affiché. */
const BENIGN_ERRORS = new Set(['no-speech', 'aborted']);
/** Au bout de combien d'échecs transitoires consécutifs on affiche enfin l'erreur. */
const TRANSIENT_ERROR_THRESHOLD = 3;

/**
 * « Moteur sourd » : le micro de l'appel capte une voix, mais le moteur de
 * reconnaissance n'a donné aucun signe de vie (ni `speechstart` ni résultat).
 * Cas réel (Chrome Mac) : `no-speech` en boucle, car Chrome écoute le micro par
 * défaut du SYSTÈME, pas forcément celui de l'appel. On échantillonne le pic
 * du micro toutes les `DEAF_CHECK_MS` ; `DEAF_PEAK_THRESHOLD` est volontairement
 * plus haut que le seuil « visuel » de `mic-level.ts` pour ne pas confondre
 * un ventilateur avec une voix.
 */
const DEAF_CHECK_MS = 500;
const DEAF_PEAK_THRESHOLD = 0.06;
const DEAF_SPEECH_MS = 3000;
/** Silence local au-delà duquel le cumul de parole repart de zéro (bruits isolés ≠ parole). */
const DEAF_PAUSE_RESET_MS = 2000;

export const ENGINE_SILENT_ERROR = 'engine-silent';

/**
 * `aborted` REÇU sur une instance encore attachée = coupée par quelqu'un
 * d'autre (nous détachons toujours avant d'appeler `abort()`). Chrome n'autorise
 * qu'UNE session de reconnaissance à la fois : deux onglets/fenêtres de la même
 * salle ouverts dans le même Chrome (test des deux côtés depuis un seul Mac) se
 * suppriment mutuellement en boucle rapide `onaudiostart` → `aborted`. Un
 * `aborted` isolé est bénin ; `ABORT_LOOP_THRESHOLD` d'affilée sans aucun
 * résultat = conflit de sessions.
 */
export const SESSION_CONFLICT_ERROR = 'session-conflict';
const ABORT_LOOP_THRESHOLD = 3;

/**
 * Message actionnable par code d'erreur — le message générique précédent
 * ("vérifie l'autorisation micro") était trompeur pour `service-not-allowed` :
 * ce code apparaît MÊME depuis un clic direct (geste utilisateur valide), il
 * ne s'agit PAS d'un problème de permission micro mais du SERVICE de
 * reconnaissance vocale lui-même bloqué. Le remède dépend de la plateforme :
 * Dictée désactivée (macOS/iOS), extension de blocage, ou — cas iPhone
 * constaté — navigateur autre que Safari (Chrome iOS n'y a jamais accès).
 * `native-*` = codes du backend NATIF (Capacitor), jamais émis par le
 * navigateur.
 */
export function transcriptionErrorGuidance(
  code: string,
  env: SpeechEnv = detectSpeechEnv(),
): string {
  switch (code) {
    case 'not-allowed':
      return 'Autorise le micro pour ce site dans les réglages de ton navigateur, puis réessaie.';
    case 'service-not-allowed':
      if (env.isIosNonSafari) {
        return "Sur iPhone/iPad, seul Safari donne accès à la reconnaissance vocale dans une page web : Chrome et les autres navigateurs iOS en sont privés, même avec la Dictée activée. Ouvre cette page dans Safari, ou utilise l'app JeRaisonne.";
      }
      if (env.isIos) {
        return 'Sur iPhone/iPad : Réglages → Général → Clavier → Dictée vocale doit être activée, et autorise ce site quand Safari te le demande. Puis réessaie.';
      }
      return "Le service de reconnaissance vocale est bloqué par ton système ou ton navigateur — pas un problème de clic. Sur Mac : Réglages Système → Clavier → Dictée doit être activé. Vérifie aussi qu'aucune extension de blocage (publicités, vie privée) n'interfère avec ce site.";
    case 'network':
      return 'Impossible de joindre le service de reconnaissance vocale — vérifie ta connexion internet. Utilise Chrome ou Safari : Brave, Arc et les navigateurs sans services Google ne sont pas compatibles.';
    case 'audio-capture':
      return 'Aucun micro détecté, ou déjà utilisé par une autre application.';
    case SESSION_CONFLICT_ERROR:
      return "Chrome n'autorise qu'UNE reconnaissance vocale à la fois et une autre session vient de couper celle-ci en boucle : la même salle est probablement ouverte dans un autre onglet ou une autre fenêtre de ce navigateur (ou une autre appli de dictée tourne). Ferme-la — pour tester les deux côtés, ouvre l'autre participant sur un autre appareil ou un autre navigateur — puis réessaie.";
    case ENGINE_SILENT_ERROR:
      return "Ton micro capte ta voix, mais le moteur de reconnaissance n'entend rien : Chrome écoute le micro par défaut du Mac (Réglages Système → Son → Entrée), qui peut différer de celui de l'appel. Ferme les autres applis qui utilisent le micro, puis réessaie.";
    case 'native-permission-denied':
      return 'Autorise le micro et la reconnaissance vocale pour JeRaisonne dans les réglages de ton téléphone, puis réessaie.';
    case 'native-not-available':
      return "La reconnaissance vocale n'est pas disponible sur cet appareil.";
    default:
      return 'Réessaie, ou vérifie ta connexion.';
  }
}

function detach(recognition: SpeechRecognitionLike): void {
  recognition.onstart = null;
  recognition.onaudiostart = null;
  recognition.onspeechstart = null;
  recognition.onnomatch = null;
  recognition.onresult = null;
  recognition.onerror = null;
  recognition.onend = null;
}

export interface UseLiveTranscriptionResult {
  /** false = navigateur sans Web Speech API (Firefox notamment) — masquer le contrôle. Toujours `true` sur natif (vérifié à `start()`, cf. `native-not-available`). */
  supported: boolean;
  active: boolean;
  /** Le moteur a réellement ouvert le micro (`audiostart`) — `active` seul ne prouve pas qu'il écoute. */
  listening: boolean;
  /** Indicateur volume micro local — juste visuel. */
  isSpeaking: boolean;
  /** Phrase en cours de reconnaissance (pas encore postée) — preuve visible que le moteur entend. Vide sur iOS/natif. */
  interimText: string;
  /** Dernier code d'erreur remonté (null tant que rien de significatif) — bascule le bouton en état visible d'échec. */
  error: string | null;
  /** Démarre — ou RELANCE proprement si déjà en cours (bouton « Réessayer »). Doit être appelé depuis un geste utilisateur. */
  start: () => void;
  stop: () => void;
}

const isNative = Capacitor.isNativePlatform();

/**
 * Retranscription LIVE : chaque participant fait tourner un moteur de
 * reconnaissance vocale sur SON PROPRE micro (déjà ouvert par WebRTC — aucun
 * second `getUserMedia`, aucun second prompt). Chaque phrase reconnue
 * déclenche `onUtterance`, qui poste le texte dans le chat — jamais d'audio
 * enregistré ni uploadé, uniquement du texte.
 *
 * DEUX backends, choisis une fois pour toutes par `isNative` (constante de
 * plateforme, jamais réévaluée en cours de session — brancher dessus À
 * L'INTÉRIEUR d'un seul jeu de hooks ne viole donc pas les rules-of-hooks,
 * même convention que `usePushNotifications`, apps/app) :
 * - **web** (`apps/tutor`) : Web Speech API du navigateur (`SpeechRecognition`).
 * - **natif** (route `/rendez-vous/$id/room` d'`apps/app`) : plugin Capacitor
 *   `@capgo/capacitor-speech-recognition` (`native-speech-recognition.ts`)
 *   — le Web Speech API est exposé mais NON FONCTIONNEL dans une WKWebView
 *   iOS, cf. le commentaire de ce fichier.
 */
export function useLiveTranscription(
  localStream: MediaStream | null,
  onUtterance: (text: string) => void,
): UseLiveTranscriptionResult {
  const supported = isNative || getSpeechRecognitionCtor() !== null;
  const [active, setActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [error, setErrorState] = useState<string | null>(null);
  const { isSpeaking, peakRef } = useMicLevel(localStream);
  const onUtteranceRef = useRef(onUtterance);
  onUtteranceRef.current = onUtterance;
  const localStreamRef = useRef(localStream);
  localStreamRef.current = localStream;

  // `errorRef` : les handlers du moteur (fermetures longues) doivent lire
  // l'erreur COURANTE, pas celle du rendu où ils ont été créés.
  const errorRef = useRef<string | null>(null);
  const setError = useCallback((code: string | null) => {
    errorRef.current = code;
    setErrorState(code);
  }, []);

  // --- backend web ---------------------------------------------------
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const wantActiveRef = useRef(false);
  const consecutiveTransientErrorsRef = useRef(0);
  const consecutiveAbortsRef = useRef(0);
  const interimRef = useRef('');
  const engineAliveRef = useRef(false);
  const deafSpeechMsRef = useRef(0);
  const deafSilenceMsRef = useRef(0);
  const deafDetectionsRef = useRef(0);
  /** Le moteur écoute la PISTE de l'appel plutôt que le micro par défaut du système (repli de « moteur sourd »). */
  const useTrackRef = useRef(false);

  const setInterim = useCallback((text: string) => {
    interimRef.current = text;
    setInterimText(text);
  }, []);

  /** Une phrase que le moteur n'a jamais finalisée ne doit pas être perdue quand la session se termine. */
  const flushInterim = useCallback(() => {
    const text = interimRef.current.trim();
    setInterim('');
    if (text) onUtteranceRef.current(text);
  }, [setInterim]);

  const liveAudioTrack = useCallback(
    () => localStreamRef.current?.getAudioTracks().find((t) => t.readyState === 'live') ?? null,
    [],
  );

  /** Ouvre UNE session de reconnaissance. Ne touche ni à l'erreur affichée ni aux compteurs : c'est aussi la relance automatique de `onend`. */
  const beginSession = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const env = detectSpeechEnv();

    const recognition = new Ctor();
    recognition.lang = 'fr-FR';
    recognition.continuous = true;
    // iOS (Safari) fonctionne tel quel : on n'y touche pas.
    recognition.interimResults = !env.isIos;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('[transcription] recognition.onstart');
    };
    recognition.onaudiostart = () => {
      console.log('[transcription] recognition.onaudiostart (micro capté)');
      setListening(true);
    };
    recognition.onspeechstart = () => {
      console.log('[transcription] recognition.onspeechstart (voix détectée)');
      engineAliveRef.current = true;
      deafSpeechMsRef.current = 0;
      consecutiveAbortsRef.current = 0;
    };
    recognition.onnomatch = () => {
      console.log('[transcription] recognition.onnomatch (son détecté, non reconnu)');
    };
    recognition.onresult = (event) => {
      console.log(
        `[transcription] onresult: ${event.results.length} résultat(s), resultIndex=${event.resultIndex}`,
      );
      // Un vrai résultat prouve que le moteur fonctionne : on lève toute alerte
      // « souple » (réseau, moteur sourd) et on repart de zéro.
      engineAliveRef.current = true;
      deafSpeechMsRef.current = 0;
      deafDetectionsRef.current = 0;
      consecutiveTransientErrorsRef.current = 0;
      consecutiveAbortsRef.current = 0;
      if (errorRef.current && !FATAL_ERRORS.has(errorRef.current)) setError(null);

      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]!;
        const text = result[0]?.transcript.trim() ?? '';
        if (result.isFinal) {
          if (text) onUtteranceRef.current(text);
        } else if (text) {
          interim = interim ? `${interim} ${text}` : text;
        }
      }
      setInterim(interim);
    };
    recognition.onerror = (event) => {
      const code = event.error;
      console.warn('[transcription] recognition error', code);

      if (code === 'aborted') {
        consecutiveAbortsRef.current += 1;
        if (consecutiveAbortsRef.current >= ABORT_LOOP_THRESHOLD) setError(SESSION_CONFLICT_ERROR);
        return;
      }
      consecutiveAbortsRef.current = 0;

      if (BENIGN_ERRORS.has(code)) {
        // 'no-speech'/'aborted' sont fréquents en usage normal — onend relance déjà.
        consecutiveTransientErrorsRef.current = 0;
        return;
      }
      if (FATAL_ERRORS.has(code)) {
        // Permission refusée : relancer en boucle contre un refus permanent
        // ne sert à rien et boucle silencieusement pour toujours — on coupe.
        wantActiveRef.current = false;
        setError(code);
        return;
      }
      // Transitoire (ex. 'network' — le moteur Chrome dépend d'un serveur
      // distant, 'audio-capture'…) : ne remonte qu'après plusieurs échecs
      // consécutifs pour ne pas alerter sur un simple blip.
      consecutiveTransientErrorsRef.current += 1;
      if (consecutiveTransientErrorsRef.current >= TRANSIENT_ERROR_THRESHOLD) {
        setError(code);
      }
    };
    recognition.onend = () => {
      setListening(false);
      flushInterim();
      // Instance déjà remplacée (relance manuelle / moteur sourd) : rien à faire.
      if (recognitionRef.current !== recognition) return;
      recognitionRef.current = null;
      // Les navigateurs arrêtent la reconnaissance après un silence prolongé
      // même en continuous=true — on relance tant que l'utilisateur n'a pas
      // explicitement arrêté (wantActiveRef).
      if (wantActiveRef.current) {
        const delay = errorRef.current ? SLOW_RESTART_DELAY_MS : RESTART_DELAY_MS;
        setTimeout(() => {
          if (wantActiveRef.current && !recognitionRef.current) beginSession();
        }, delay);
      } else {
        setActive(false);
      }
    };

    try {
      const track = useTrackRef.current ? liveAudioTrack() : null;
      if (track) recognition.start(track);
      else recognition.start();
      recognitionRef.current = recognition;
      setActive(true);
    } catch (err) {
      // start() peut lever si déjà démarrée ailleurs — onend/prochain start() rattrape.
      console.error('[transcription] recognition.start() failed', err);
    }
  }, [flushInterim, liveAudioTrack, setError, setInterim]);

  /** Remplace l'instance courante sans déclencher sa logique de fin (aucune phrase perdue : `flushInterim` d'abord). */
  const replaceSession = useCallback(() => {
    const old = recognitionRef.current;
    if (old) {
      detach(old);
      recognitionRef.current = null;
      try {
        old.abort();
      } catch {
        // déjà arrêtée
      }
    }
    beginSession();
  }, [beginSession]);

  const startWeb = useCallback(() => {
    if (!supported) return;
    wantActiveRef.current = true;
    setError(null);
    consecutiveTransientErrorsRef.current = 0;
    consecutiveAbortsRef.current = 0;
    engineAliveRef.current = false;
    deafSpeechMsRef.current = 0;
    deafSilenceMsRef.current = 0;
    deafDetectionsRef.current = 0;
    // Déjà en cours (« Réessayer » sur une alerte souple) → relance propre.
    if (recognitionRef.current) flushInterim();
    replaceSession();
  }, [supported, setError, flushInterim, replaceSession]);

  const stopWeb = useCallback(() => {
    wantActiveRef.current = false;
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setActive(false);
    setListening(false);
  }, []);

  // Détecteur de « moteur sourd » (web hors iOS — Safari iOS marche, on n'y
  // touche pas) : ~3 s de parole locale quasi continue sans aucun signe de vie
  // du moteur → 1re fois on le relance en lui donnant la PISTE de l'appel (ou
  // à l'inverse le micro système si c'est déjà la piste), 2e fois on affiche
  // l'erreur `engine-silent` (souple : le moteur continue, un résultat la lève).
  useEffect(() => {
    if (isNative || !active || detectSpeechEnv().isIos) return undefined;
    const id = setInterval(() => {
      if (engineAliveRef.current || errorRef.current) return;
      const peak = peakRef.current;
      peakRef.current = 0;
      if (peak > DEAF_PEAK_THRESHOLD) {
        deafSpeechMsRef.current += DEAF_CHECK_MS;
        deafSilenceMsRef.current = 0;
      } else {
        deafSilenceMsRef.current += DEAF_CHECK_MS;
        if (deafSilenceMsRef.current >= DEAF_PAUSE_RESET_MS) deafSpeechMsRef.current = 0;
      }
      if (deafSpeechMsRef.current < DEAF_SPEECH_MS) return;

      deafSpeechMsRef.current = 0;
      if (deafDetectionsRef.current === 0 && (useTrackRef.current || liveAudioTrack())) {
        deafDetectionsRef.current = 1;
        useTrackRef.current = !useTrackRef.current;
        console.warn(
          `[transcription] moteur sourd — relance avec ${useTrackRef.current ? 'la piste de l’appel' : 'le micro système'}`,
        );
        replaceSession();
      } else {
        console.warn('[transcription] moteur sourd — abandon, erreur affichée');
        setError(ENGINE_SILENT_ERROR);
      }
    }, DEAF_CHECK_MS);
    return () => clearInterval(id);
  }, [active, peakRef, liveAudioTrack, replaceSession, setError]);

  // --- backend natif (Capacitor) --------------------------------------
  const nativeListeningRef = useRef(false);
  const lastPartialRef = useRef('');

  const startNativeListening = useCallback(() => {
    if (nativeListeningRef.current) return;
    void NativeSpeechRecognition.start({
      language: 'fr-FR',
      partialResults: true,
      popup: false,
    })
      .then(() => {
        nativeListeningRef.current = true;
        setActive(true);
        setListening(true);
      })
      .catch((err: unknown) => {
        console.error('[transcription] native start() failed', err);
        wantActiveRef.current = false;
        nativeListeningRef.current = false;
        setActive(false);
        setListening(false);
        setError('native-error');
      });
  }, [setError]);

  const startNative = useCallback(() => {
    setError(null);
    wantActiveRef.current = true;
    void (async () => {
      try {
        const avail = await NativeSpeechRecognition.available();
        if (!avail.available) {
          wantActiveRef.current = false;
          setError('native-not-available');
          return;
        }
        const perm = await NativeSpeechRecognition.requestPermissions();
        if (perm.speechRecognition !== 'granted') {
          wantActiveRef.current = false;
          setError('native-permission-denied');
          return;
        }
        startNativeListening();
      } catch (err) {
        console.error('[transcription] native permission/availability check failed', err);
        wantActiveRef.current = false;
        setError('native-error');
      }
    })();
  }, [startNativeListening, setError]);

  const stopNative = useCallback(() => {
    wantActiveRef.current = false;
    nativeListeningRef.current = false;
    void NativeSpeechRecognition.stop().catch((err: unknown) => {
      console.error('[transcription] native stop() failed', err);
    });
    setActive(false);
    setListening(false);
  }, []);

  // Écoute des events du plugin natif — no-op sur web (jamais d'appel au
  // bridge). `listeningState:'stopped'` = fin d'énoncé (silence détecté) OU
  // stop() explicite : poste le dernier `partialResults` connu, puis relance
  // tant que l'utilisateur n'a pas explicitement arrêté (même logique de
  // relance auto que le backend web, `wantActiveRef`).
  useEffect(() => {
    if (!isNative) return undefined;

    const offPartial = NativeSpeechRecognition.addListener('partialResults', ({ matches }) => {
      const text = matches?.[0]?.trim();
      if (text) lastPartialRef.current = text;
    });
    const offState = NativeSpeechRecognition.addListener('listeningState', ({ status }) => {
      if (status !== 'stopped') return;
      nativeListeningRef.current = false;
      const text = lastPartialRef.current;
      lastPartialRef.current = '';
      if (text) onUtteranceRef.current(text);

      if (wantActiveRef.current) {
        setTimeout(() => {
          if (wantActiveRef.current) startNativeListening();
        }, RESTART_DELAY_MS);
      } else {
        setActive(false);
        setListening(false);
      }
    });

    return () => {
      void offPartial.then((h) => h.remove());
      void offState.then((h) => h.remove());
    };
  }, [startNativeListening]);

  const start = isNative ? startNative : startWeb;
  const stop = isNative ? stopNative : stopWeb;

  useEffect(() => stop, [stop]);

  return { supported, active, listening, isSpeaking, interimText, error, start, stop };
}
